import ExpiredVersion from "@/components/layout/expired-version/ExpiredVersion";
import Updater from "@/components/layout/updater/Updater";
import {
  useAuthActions,
  useAuthLoading,
  useAuthUser,
  useAuthStore,
} from "@/context/auth/AuthContext";
import { can, withOwnerOnly, type AuthzAction } from "@/context/auth/authz";
import { meUserWithOrg, normalizeIdentityUser } from "@/context/auth/authUtils";
import { useAxios } from "@/context/axios/AxiosContext";
import { IpcRest } from "@/hooks/ipc";
import { useDisclosure } from "@/hooks/useDisclosure";
import usePermissions from "@/hooks/usePermissions";
import loadStore from "@/utils/generateStore";
import { updater as platformUpdater } from "@/platform";
import type { UpdateCheckResult } from "@/platform/types";
import { isBefore } from "date-fns";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { urlDeLoginCon } from "@/utils/nextDestination";
import { readActAsSession } from "@/context/auth/authUtils";
import {
  esRutaDeLaCaja,
  esRutaDelMostrador,
  esSesionDeCaja,
  rutaDeLaCaja,
} from "@/context/caja/sesionDeCaja";
import NoAuthorized from "../no-authorized/NoAuthorized";
import NoAccessPlan from "../no-access-plan/NoAccessPlan";
import LoadingScreenPage from "./loading-screen/LoadingScreenPage";
import Page from "./page/Page";
import { AppSidebar } from "./sidebar/Sidebar";
import BuyerWelcome from "./buyer-welcome/BuyerWelcome";
import AvisoDeMora from "./aviso-de-mora/AvisoDeMora";
import BillingAccessBoundary from "./billing-access/BillingAccessBoundary";
import NegocioSuspendido from "./billing-access/NegocioSuspendido";
import { enModoLectura } from "./billing-access/modoLectura";
import { formatDate } from "@/utils/formatDate";
import { FiltersDialog } from "../dialogs/filters/FiltersDialog";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { LoadingContainerStyled } from "../loading/LoadingContainer";
import Loading from "../loading/Loading";
import { Alert, AlertDescription } from "../ui/alert";
import { Separator } from "../ui/separator";
import Input from "../input/Input";
import { DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { useLatidoDeLaCaja } from "@/hooks/caja/useLatidoDeLaCaja";
import { useDrenadorDeLaCaja } from "@/hooks/caja/useDrenadorDeLaCaja";
import { useSeguirLaFotoDeLaCaja } from "@/hooks/caja/useSeguirLaFotoDeLaCaja";
import { useRefrescarLaCajaOffline } from "@/hooks/caja/useRefrescarLaCajaOffline";
import RecuperacionDeTickets from "./caja/RecuperacionDeTickets";
import { useActualizacionEnEspera } from "./updater/useActualizacionEnEspera";

interface Props {
  children: React.ReactNode;
  /**
   * @deprecated Guard por STRING DE RUTA (vía `usePermissions`→`paths.ts`, con
   * pérdida). Se migra a `resource`/`action` (resource id del entitlement). #49.
   */
  pathname?: string;
  /** @deprecated Usar `action` (view/modify/delete) con `resource`. */
  permission?: "read" | "write" | "delete";
  /**
   * Guard canónico: id(s) de recurso del entitlement. Cuando está presente, el
   * acceso lo decide `can(user, resource, action)` — mismo vocab que el editor,
   * el menú y el borde. Tiene prioridad sobre `pathname`/`permission`.
   */
  resource?: string | string[];
  action?: AuthzAction;
  noSidebar?: boolean;
  planFeature?: string | string[];
  /**
   * Sólo el dueño del negocio entra. Se suma al guard de arriba, no lo
   * reemplaza (ver `withOwnerOnly`). El ítem del menú tiene que declarar lo
   * mismo.
   */
  ownerOnly?: boolean;
}

export default function ClientLayout({
  children,
  pathname,
  permission = "read",
  resource,
  action = "view",
  noSidebar = false,
  planFeature,
  ownerOnly = false,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const { onOpen, isOpen, onClose } = useDisclosure();
  const [updateParseado, setUpdateParseado] = useState<any>(false);
  const [originalUpdate, setOriginalUpdate] =
    useState<UpdateCheckResult | null>(null);
  const {
    canRead,
    canModify,
    canDelete,
    isOwner,
    hasFeatureAccess,
    planAccessReason,
    requiredFeature,
    isInTrial,
  } = usePermissions(pathname ?? "", planFeature);
  const { finishLogin, loginSuccess, logout } = useAuthActions();
  // Impersonación (#92): si la sesión es act-as tiene que verse SIEMPRE.
  const actAs = readActAsSession();
  const isLoginLoading = useAuthLoading();
  const contextUser = useAuthUser();

  // Guard canónico: si la ruta declara `resource`, el acceso lo decide `can()`
  // (owner/plan/clinical/grant, vocab view/modify/delete). `null` = ruta todavía
  // legacy → cae al camino `usePermissions`/`pathname` de abajo.
  const resourceAllowed =
    resource != null ? can(contextUser, resource, action) : null;
  const axios = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isError, setIsError] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [extraComments, setExtraComments] = useState("");

  const [authorized, setAuthorized] = useState(false);
  // Una autorización de la sesión anterior no puede montar la operación
  // mientras se lee el token de la nueva (PlatformStore también es asíncrono).
  const [verifiedUser, setVerifiedUser] = useState<unknown>(null);
  const hasVerifiedSession =
    contextUser != null && verifiedUser === contextUser;
  const isDemo = contextUser?.isDemo;
  const expirationDate = contextUser?.demoExpirationAt;

  const isExpired = !isDemo
    ? false
    : isBefore(new Date(expirationDate), new Date());

  // La caja offline del escritorio: el latido y la cola, por el negocio de la
  // sesión; y el flag que dice si la caja se prende en la próxima apertura.
  useLatidoDeLaCaja();
  useDrenadorDeLaCaja();
  useSeguirLaFotoDeLaCaja();
  useRefrescarLaCajaOffline();

  const actualizacionEnEspera = useActualizacionEnEspera(
    !!updateParseado?.available
  );

  const checkForUpdate = async () => {
    const result = await platformUpdater.checkForUpdate();

    if (result && result.available && result.version) {
      setOriginalUpdate(result);
      setUpdateParseado({ available: true, version: result.version });
    }
  };

  // useEffect(() => {
  //   if (!contextUser || !contextuser?.businessId) {
  //     navigate("/login");
  //   }
  // }, [])

  useEffect(() => {
    checkForUpdate();
  }, []);

  const checkPermissions = (
    canRead: boolean,
    canModify: boolean,
    canDelete: boolean,
    isOwner: boolean,
    hasFeatureAccess: boolean
  ) => {
    // Primero verificar acceso por plan
    if (!hasFeatureAccess) {
      return false;
    }

    // Luego verificar permisos tradicionales
    if (isOwner) return true;

    switch (permission) {
      case "read":
        return canRead;
      case "write":
        return canModify;
      case "delete":
        return canDelete;
      default:
        return canRead;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const isStale = () =>
      cancelled ||
      (useAuthStore.getState() as { user: unknown }).user !== contextUser;
    const checkAccess = async () => {
      setIsLoading(true);
      try {
        const store = await loadStore();
        const token = await store.get("token");
        if (isStale()) return;

        if (!token) {
          // La caja sin sesión: el cajero entró con su PIN, y sólo se abre el
          // mostrador del local de la caja (trd-client#346, D23).
          if (esSesionDeCaja(contextUser)) {
            const caja = contextUser.sesionDeCaja;
            if (!esRutaDeLaCaja(caja, location.pathname)) {
              navigate(rutaDeLaCaja(caja), { replace: true });
              // El mostrador de otro local es esta misma ruta: este layout no
              // se vuelve a montar, así que la autoriza él. Otra pantalla la
              // decide el layout del mostrador.
              if (!esRutaDelMostrador(location.pathname)) return;
            }
            setAuthorized(true);
            setVerifiedUser(contextUser);
            finishLogin();
            setIsLoading(false);
            return;
          }
          // Conservamos a dónde iba: si llegó por un link de un mail (el que
          // core le manda al Gran Proveedor apunta a /ventas-b2b/solicitudes),
          // después de entrar tiene que caer ahí y no en la home.
          navigate(urlDeLoginCon(`${location.pathname}${location.search}`));
          return;
        }

        // Con token, el usuario de una caja sin sesión ya no vale: el de la
        // sesión sale del /me.
        if (!contextUser || esSesionDeCaja(contextUser)) {
          const me = await IpcRest.get<any>({ url: "/api/v1/auth/me" });
          const meUser = meUserWithOrg(me);
          if (isStale()) return;
          await store.set("user", meUser);
          if (isStale()) return;
          loginSuccess(meUser, token);
          return;
        }

        const normalized = normalizeIdentityUser(contextUser, token);
        if (
          normalized.suspended !== contextUser.suspended ||
          normalized.modules !== contextUser.modules ||
          normalized.isClinician !== contextUser.isClinician
        ) {
          loginSuccess(contextUser, token);
          // La próxima ejecución decide con el user ya normalizado, nunca
          // con permisos calculados antes de leer el token nativo.
          return;
        }

        setAuthorized(
          withOwnerOnly(
            contextUser,
            ownerOnly,
            resourceAllowed ??
              checkPermissions(
                canRead,
                canModify,
                canDelete,
                isOwner,
                hasFeatureAccess
              )
          )
        );
        setVerifiedUser(contextUser);
        finishLogin();
        setIsLoading(false);
      } catch (error) {
        if (isStale()) return;
        console.error("Error checking access:", error);
        setIsLoading(false);
        navigate(urlDeLoginCon(`${location.pathname}${location.search}`));
      }
    };

    checkAccess();
    return () => {
      cancelled = true;
    };
  }, [
    contextUser,
    resourceAllowed,
    canRead,
    canModify,
    canDelete,
    isOwner,
    hasFeatureAccess,
    permission,
    ownerOnly,
    loginSuccess,
    finishLogin,
  ]);

  const handleEmailChange = (e: any) => {
    // validate if the email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(!emailRegex.test(e.target.value));
    setEmail(e.target.value);
  };

  const handleExtraComments = (e: any) => {
    setExtraComments(e.target.value);
  };

  const onSendData = async () => {
    const messageToSend = extraComments
      ? `El mail ${email} quiere renovar la demo y dice: ${extraComments}`
      : `El mail ${email} quiere renovar la demo y no tiene comentarios adicionales para hacer`;

    setIsSending(true);
    setIsSent(false);
    setIsError(false);

    try {
      const response = await axios.post("/messaging/demo-info", {
        message: messageToSend,
      });

      if (response.status >= 200 && response.status < 300) {
        setIsSent(true);
        setIsError(false);
        setIsSending(false);
      } else {
        throw new Error("Error sending the message");
      }
    } catch {
      setIsSent(false);
      setIsError(true);
      setIsSending(false);
    }
  };

  // La app de gestión (trd-client) es solo para negocios. Un buyer (cliente
  // final) no ve el sidebar ni ningún módulo: solo una pantalla única con la
  // identidad de Trud y el logout. Se chequea apenas /auth/me resolvió el user.
  if (contextUser?.type === "buyer") {
    return <BuyerWelcome />;
  }

  // Qué se monta en la página. Una sola cadena para las dos ramas (con y sin
  // sidebar): estaba copiada, y una regla nueva podía quedar en una sola.
  //
  // El empleado de un negocio suspendido va primero: con la app en modo
  // lectura, una pantalla que no se abre es por la suspensión, no por el plan
  // ni por su grant (trd-client#269). Sin sidebar, el upsell del plan sólo
  // aplica a las rutas legacy, sin `resource`.
  const contenido =
    isLoginLoading || isLoading ? (
      <div className="flex flex-col items-center justify-center h-full">
        <LoadingScreenPage />
      </div>
    ) : !authorized && enModoLectura(contextUser) ? (
      <NegocioSuspendido />
    ) : !authorized && planAccessReason && (!noSidebar || resource == null) ? (
      <NoAccessPlan
        reason={planAccessReason}
        requiredFeature={requiredFeature}
        isInTrial={isInTrial}
      />
    ) : !authorized ? (
      <NoAuthorized />
    ) : (
      <>{children}</>
    );

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <div className="bg-background-100">
        {actAs.isActAs && (
          <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-1 px-3 py-2 bg-amber-400 text-black">
            <p className="font-medium">
              Estás viendo la app como{" "}
              {actAs.impersonatedEmail || contextUser?.email || "otro usuario"}
            </p>
            <button
              type="button"
              className="underline cursor-pointer font-medium"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
            >
              Salir
            </button>
          </div>
        )}
        <AvisoDeMora />
        {isDemo && (
          <div className="flex justify-center items-center gap-[5px] h-[35px] bg-orange-300">
            <p>
              Estás usando una versión de prueba de Trud. Esta versión expirará
              el día {formatDate(expirationDate, "dd 'de' MMMM 'de' yyyy")}.
            </p>
            <p className="underline cursor-pointer" onClick={onOpen}>
              Más información
            </p>

            <FiltersDialog
              isOpen={isOpen}
              onClose={onClose}
              title="Información"
            >
              {isSending && (
                <LoadingContainerStyled>
                  <Loading />
                </LoadingContainerStyled>
              )}

              {isSent && (
                <div className="space-y-4">
                  <Alert variant="success">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      Hemos recibido tu solicitud. ¡Gracias por confiar en Trud!
                    </AlertDescription>
                  </Alert>

                  <div className="flex flex-col gap-2.5 text-center">
                    <p>
                      En breve, recibirás un correo electrónico con la
                      información necesaria para renovar tu suscripción.
                    </p>
                  </div>
                </div>
              )}

              {isError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Ocurrió un error al enviar tu solicitud. Por favor,
                    inténtalo de nuevo.
                  </AlertDescription>
                </Alert>
              )}

              {!isSending && !isSent && !isError && (
                <div className="flex flex-col gap-5">
                  <p className="text-lg font-bold">
                    ¡Nos alegra que estés disfrutando de Trud!
                  </p>

                  {isExpired ? (
                    <p>La versión de prueba ha expirado.</p>
                  ) : (
                    <p>
                      Estás utilizando la versión de prueba, la cual tiene como
                      fecha límite el{" "}
                      {formatDate(expirationDate, "dd 'de' MMMM 'de' yyyy")}.
                    </p>
                  )}

                  <Separator />

                  <p>
                    Para seguir disfrutando de todas las funcionalidades sin
                    restricciones, te invitamos a adquirir la versión completa
                    completando tu correo electrónico a continuación:
                  </p>

                  <Input
                    id="email"
                    value={email}
                    onChange={handleEmailChange}
                    type="email"
                    placeholder="Ingrese su correo electrónico aquí..."
                    className={emailError ? "border-red-500" : ""}
                  />
                  {emailError && (
                    <p className="text-sm text-red-500">
                      Por favor, ingrese un correo electrónico válido.
                    </p>
                  )}

                  <p>
                    ¡Tu opinión es importante para nosotros! Gracias a tus
                    comentarios, Trud sigue creciendo y mejorando.
                  </p>

                  <p>
                    Por eso, si tienes sugerencias o has encontrado algún fallo,
                    no dudes en informarnos, ya que eso nos permite hacer que la
                    gestión de tu negocio sea más fácil y eficiente cada día.
                  </p>

                  <textarea
                    id="extraComments"
                    value={extraComments}
                    onChange={handleExtraComments}
                    placeholder="Ingrese aquí sus comentarios adicionales..."
                    className="min-h-[100px]"
                  />
                </div>
              )}

              <DialogFooter className="gap-2">
                {!isSending && !isSent && !isError && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        onClose();
                        setIsError(false);
                        setIsSent(false);
                        setIsSending(false);
                      }}
                    >
                      Cerrar
                    </Button>

                    <Button
                      onClick={onSendData}
                      disabled={email.trim().length === 0 || emailError}
                    >
                      Enviar datos
                    </Button>
                  </>
                )}

                {(isSent || isError) && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        onClose();
                        setIsError(false);
                        setIsSent(false);
                        setIsSending(false);
                      }}
                    >
                      Cerrar
                    </Button>

                    {isError && (
                      <Button onClick={onSendData}>Volver a intentar</Button>
                    )}
                  </>
                )}
              </DialogFooter>
            </FiltersDialog>
          </div>
        )}

        <div
          className={`flex min-h-[${
            isDemo ? "calc(100vh-35px)" : "100vh"
          }] h-[${
            isDemo ? "calc(100vh-35px)" : "100vh"
          }] bg-background-100 overflow-hidden relative text-text-primary mx-auto`}
        >
          {!isExpired &&
            !actualizacionEnEspera &&
            updateParseado &&
            updateParseado.available &&
            updateParseado.version && (
              <Updater
                update={originalUpdate}
                updateParseado={updateParseado}
              />
            )}

          {isDemo && isExpired && <ExpiredVersion onOpenModal={onOpen} />}

          {hasVerifiedSession && <RecuperacionDeTickets />}

          {!hasVerifiedSession ? (
            <LoadingScreenPage />
          ) : (
            <BillingAccessBoundary>
              {noSidebar && (
                <Page isDemo={isDemo} noSidebar={noSidebar}>
                  {contenido}
                </Page>
              )}

              {!noSidebar && (
                <AppSidebar>
                  <Page isDemo={isDemo}>{contenido}</Page>
                </AppSidebar>
              )}
            </BillingAccessBoundary>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}
